import { useCallback, useState } from "react";

// The collections sidebar shows one level at a time, so its whole navigation
// state is the path down to that level: collection ids, outermost first, empty
// at the top. Keyed by space — browsing someone else's collections must not
// move where you were in your own.

// Deliberately **not** persisted. Arriving at a space is arriving at the top of
// its collection tree; a level restored from an earlier visit is a place the
// user did not ask to be, against a tree that may have changed since. The path
// therefore lives only as long as the sidebar does.

// Nothing here is pruned against a live tree: a path naming a collection since
// deleted or re-parented simply stops resolving, and the level it reached is
// shown instead. The bound is far above any real hierarchy.
const MAX_DEPTH = 32;

const same = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * Which level of the collection graph the sidebar is showing.
 *
 * Deliberately not in Redux: nothing outside the sidebar reads it.
 *
 * There is one setter rather than `open`/`up` helpers on purpose. The caller
 * moves relative to the path it actually *resolved* (see `resolveLevel`), not
 * the one it holds, so a stale segment cannot leave "back" pointing at the
 * level already on screen. `goTo([])` is how a caller returns to the top.
 */
export function useCollectionPath(space: string) {
  const [state, setState] = useState<{ space: string; path: string[] }>(() => ({
    space,
    path: [],
  }));

  // Switching spaces starts that space at its top level, rather than carrying
  // the current path over into a tree it does not describe.
  if (state.space !== space) {
    setState({ space, path: [] });
  }

  const goTo = useCallback(
    (path: string[]) =>
      setState((previous) =>
        // Re-rendering for a move that changes nothing would restart every
        // downstream memo for no reason.
        same(previous.path, path)
          ? previous
          : { ...previous, path: path.slice(0, MAX_DEPTH) },
      ),
    [],
  );

  return { path: state.path, goTo };
}
